// Deep One activations, replayed one at a time. The engine activates them in rule order but the
// whole activation reaches the table in one update; this rewinds the board to before it (undoing
// the Deep One moves, damage and passenger losses that followed each "deepOneAct" event) and then
// steps forward one Deep One per beat, with a caption saying which one acts and what it does.
// Each beat has two parts: first the acting Deep One is marked (caption + ring), then it acts.
import { useEffect, useMemo, useRef, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import css from "./Unfathomable.module.css";

/** Marking the Deep One that is about to act, then its action playing out. */
const kAimMs = 800;
const kActMs = 1500;

type Act = Extract<uf.GameEvent, { type: "deepOneAct" }>;
type Spaces = uf.GameView["spaces"];
interface Step { act: Act; events: uf.GameEvent[] }
interface Playback { before: Spaces; steps: Step[]; i: number; acting: boolean }

const isSpace = (spaces: Spaces, x: string): x is uf.SpaceId => x in spaces;

/** Apply (dir = 1) or undo (dir = −1) one event's effect on the spaces we stage. */
function applyEvent(spaces: Spaces, ev: uf.GameEvent, dir: 1 | -1): void {
  const bump = (sp: string, field: "deepOnes" | "passengers", by: number) => {
    if (isSpace(spaces, sp)) spaces[sp] = { ...spaces[sp], [field]: Math.max(0, spaces[sp][field] + by) };
  };
  if (ev.type === "monster" && ev.what === "deepOne") {
    bump(ev.from, "deepOnes", -ev.count * dir);
    bump(ev.to, "deepOnes", ev.count * dir);
  } else if (ev.type === "damage") {
    spaces[ev.space] = { ...spaces[ev.space], damage: dir === 1 ? ev.card : null };
  } else if (ev.type === "defeat" && ev.target === "passenger" && ev.space) {
    bump(ev.space, "passengers", -dir);
  }
}

function actText(view: uf.GameView, a: Act): string {
  const here = uf.spaceName(a.space);
  switch (a.act) {
    case "attack": return `${here}：攻击 ${a.target !== undefined ? view.players[a.target]?.name ?? "" : ""}`;
    case "passenger": return `${here}：袭击乘客`;
    case "damage": return `${here}：破坏了这个空间`;
    case "move": return `${here} → ${a.to ? uf.spaceName(a.to) : ""}`;
    case "blocked": return `${here}：被封锁的舱口挡住`;
    case "stay": return `${here}：原地不动`;
  }
}

/**
 * The view the 3D table should show while an activation replays (spaces rewound and stepped
 * forward), the acting space, and a caption; `busy` while it plays (decisions wait for it).
 */
export function useDeepOnePlayback(view: uf.GameView) {
  const seen = useRef(view.events.at(-1)?.seq ?? 0);
  const [play, setPlay] = useState<Playback | null>(null);

  useEffect(() => {
    const fresh = view.events.filter(e => e.seq > seen.current);
    if (fresh.length === 0) return;
    seen.current = fresh[fresh.length - 1].seq;
    const steps: Step[] = [];
    for (const { ev } of fresh) {
      if (ev.type === "deepOneAct") steps.push({ act: ev, events: [] });
      else if (steps.length > 0) steps[steps.length - 1].events.push(ev);
    }
    // A new batch replaces whatever was still playing (its end state is already in the view).
    if (steps.length === 0) { setPlay(null); return; }
    const before: Spaces = { ...view.spaces };
    for (const s of [...steps].reverse()) for (const ev of [...s.events].reverse()) applyEvent(before, ev, -1);
    setPlay({ before, steps, i: 0, acting: false });
  }, [view.events]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark, act, next Deep One; after the last one the real view takes over.
  useEffect(() => {
    if (!play) return;
    const t = setTimeout(() => setPlay(p => {
      if (!p) return null;
      if (!p.acting) return { ...p, acting: true };
      return p.i + 1 < p.steps.length ? { ...p, i: p.i + 1, acting: false } : null;
    }), play.acting ? kActMs : kAimMs);
    return () => clearTimeout(t);
  }, [play]);

  const sceneView = useMemo(() => {
    if (!play) return view;
    const spaces: Spaces = { ...play.before };
    // Earlier steps are done; step i takes effect only once its Deep One has been marked.
    for (let k = 0; k < play.i + (play.acting ? 1 : 0); k++) for (const ev of play.steps[k].events) applyEvent(spaces, ev, 1);
    return { ...view, spaces };
  }, [view, play]);

  const act = play ? play.steps[play.i].act : null;
  return { sceneView, act, caption: act ? actText(view, act) : null, busy: !!play };
}

/** The caption under the top bar while Deep Ones activate. */
export function DeepOneCaption({ act, caption }: { act: Act | null; caption: string | null }) {
  if (!act || !caption) return null;
  return (
    <div key={`${act.step}-${act.space}`} className={css.deepOneCaption} data-act={act.act}>
      <span className={css.deepOneCaptionStep}>🐟 深潜者激活 {act.step}/{act.total}</span>
      <span>{caption}</span>
    </div>
  );
}
